import { createTheme } from '@stylexjs/stylex';
import { colors } from './colors.stylex';
import { productAccents } from './palette.stylex';
import type { QomponentsTheme } from '../constants';

const darkPalette = {
  checkboxImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23c2bcb0' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxDisabledImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23606061' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23c2bcb0' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  checkboxDisabledIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23606061' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  modalBorder: 'oklch(55.1% 0.027 264.364)',
  tabBorder: 'oklch(37.3% 0.034 259.733)',
  tabHover: 'oklch(37.3% 0.034 259.733)',
  tabLabelActive: '#e6ecf2',
  segmentBorderInactive: '#484f58',
  segmentBackground: '#151b23',
  segmentHover: '#262c36',
  toolbarHover: 'rgb(239 239 239 / 30%)',
  toolbarSelected: '#223249',
  toolbarSelectedPressed: 'rgb(34 50 73 / 30%)',
  alertBackground: '#262c36',
  warningText: '#262c36',
  badgeNewBackground: '#664d03',
  badgeNewText: '#fce7ce',
  badgePositiveBackground: '#1e4032',
  badgePositiveText: '#1fcfa6',
  badgePositiveLabelText: '#000000',
  badgeNeutralBackground: 'rgb(17 24 39 / 40%)',
  badgeErrorBackground: '#44190f',
  panelBorder: 'oklch(55.1% 0.027 264.364)',
  panelBackground: '#151b23',
  selectionBackground: '#262c36',
  selectionText: '#ffffff',
  trackBackground: '#223249',
  bodyText: '#f0f6fc',
  swatchBorder: 'oklch(55.1% 0.027 264.364)',
  menuBorder: 'oklch(55.1% 0.027 264.364)',
  menuText: '#ffffff',
  menuHover: '#151b23',
  surfaceHover: '#151b23',
  surfaceSelected: '#262c36',
  surfacePressed: '#262c36',
  navigationTextActive: '#e6ecf2',
  iconMuted: '#606061',
  background: '#0d1117',
  fieldBackground: '#0d1117',
  backgroundDisabled: '#223249',
  border: '#223249',
  borderInvalid: '#d9534f',
  borderDisabled: '#223249',
  text: '#e6ecf2',
  textDisabled: 'rgb(230 236 242 / 30%)',
  placeholder: 'rgb(230 236 242 / 30%)',
};

const topicLight = createTheme(colors, {
  tabLabel: productAccents.topicLight,
  segmentBorder: '#1f5f9f',
  segmentSelected: '#d9ecff',
  segmentSelectedHover: '#c7e3ff',
  alertBackground: `color-mix(in srgb, ${productAccents.topicHighlight} 10%, transparent)`,
  accent: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  navigationTextActive: productAccents.topicLight,
  accentSubtle: productAccents.topicSubtle,
  link: productAccents.topicLight,
  icon: productAccents.topicIcon,
  iconForeground: productAccents.topicIcon,
  borderHover: productAccents.topicLight,
  borderFocus: productAccents.topicLight,
  borderActive: productAccents.topicLight,
});
const topicDark = createTheme(colors, {
  tabLabel: productAccents.topicDark,
  segmentBorder: productAccents.topicHighlight,
  segmentSelected: 'color-mix(in srgb, #41a5e1 54%, #151b23)',
  segmentSelectedHover: 'color-mix(in srgb, #41a5e1 64%, #151b23)',
  accent: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  accentSubtle: productAccents.topicSubtle,
  link: productAccents.topicLight,
  icon: productAccents.topicIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.topicDark,
  borderFocus: productAccents.topicDark,
  borderActive: productAccents.topicDark,
});
const analysisLight = createTheme(colors, {
  tabLabel: productAccents.analysisLight,
  segmentBorder: productAccents.analysisLight,
  segmentSelected: productAccents.analysisSubtle,
  segmentSelectedHover: '#dff2ee',
  alertBackground: `color-mix(in srgb, ${productAccents.analysisHighlight} 10%, transparent)`,
  accent: productAccents.analysisLight,
  accentHover: productAccents.analysisHighlight,
  accentPressed: productAccents.analysisDark,
  navigationTextActive: productAccents.analysisLight,
  accentSubtle: productAccents.analysisSubtle,
  link: productAccents.analysisLight,
  icon: productAccents.analysisIcon,
  iconForeground: productAccents.analysisIcon,
  borderHover: productAccents.analysisLight,
  borderFocus: productAccents.analysisLight,
  borderActive: productAccents.analysisLight,
});
const analysisDark = createTheme(colors, {
  tabLabel: productAccents.analysisDark,
  segmentBorder: productAccents.analysisHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.analysisHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.analysisHighlight} 64%, #151b23)`,
  accent: productAccents.analysisLight,
  accentHover: productAccents.analysisHighlight,
  accentPressed: productAccents.analysisDark,
  accentSubtle: productAccents.analysisSubtle,
  link: productAccents.analysisLight,
  icon: productAccents.analysisIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.analysisDark,
  borderFocus: productAccents.analysisDark,
  borderActive: productAccents.analysisDark,
});
const datalabLight = createTheme(colors, {
  tabLabel: productAccents.datalabLight,
  segmentBorder: '#b95b22',
  segmentSelected: '#ffe6d6',
  segmentSelectedHover: '#ffd9bf',
  alertBackground: `color-mix(in srgb, ${productAccents.datalabHighlight} 10%, transparent)`,
  accent: productAccents.datalabLight,
  accentHover: productAccents.datalabHighlight,
  accentPressed: productAccents.datalabDark,
  navigationTextActive: productAccents.datalabLight,
  accentSubtle: productAccents.datalabSubtle,
  link: productAccents.datalabLight,
  icon: productAccents.datalabIcon,
  iconForeground: productAccents.datalabIcon,
  borderHover: productAccents.datalabLight,
  borderFocus: productAccents.datalabLight,
  borderActive: productAccents.datalabLight,
});
const datalabDark = createTheme(colors, {
  tabLabel: productAccents.datalabDark,
  segmentBorder: productAccents.datalabHighlight,
  segmentSelected: 'color-mix(in srgb, #e58a52 54%, #151b23)',
  segmentSelectedHover: 'color-mix(in srgb, #e58a52 64%, #151b23)',
  accent: productAccents.datalabLight,
  accentHover: productAccents.datalabHighlight,
  accentPressed: productAccents.datalabDark,
  accentSubtle: productAccents.datalabSubtle,
  link: productAccents.datalabLight,
  icon: productAccents.datalabIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.datalabDark,
  borderFocus: productAccents.datalabDark,
  borderActive: productAccents.datalabDark,
});
const vantageLight = createTheme(colors, {
  tabLabel: productAccents.vantageLight,
  segmentBorder: productAccents.vantageLight,
  segmentSelected: productAccents.vantageSubtle,
  segmentSelectedHover: '#c5addd',
  alertBackground: `color-mix(in srgb, ${productAccents.vantageHighlight} 10%, transparent)`,
  accent: productAccents.vantageLight,
  accentHover: productAccents.vantageHighlight,
  accentPressed: productAccents.vantageDark,
  navigationTextActive: productAccents.vantageLink,
  accentSubtle: productAccents.vantageSubtle,
  link: productAccents.vantageLink,
  icon: productAccents.vantageIcon,
  iconForeground: productAccents.vantageIcon,
  borderHover: productAccents.vantageLight,
  borderFocus: productAccents.vantageLight,
  borderActive: productAccents.vantageLight,
});
const vantageDark = createTheme(colors, {
  tabLabel: productAccents.vantageDark,
  segmentBorder: productAccents.vantageHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.vantageHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.vantageHighlight} 64%, #151b23)`,
  accent: productAccents.vantageLight,
  accentHover: productAccents.vantageHighlight,
  accentPressed: productAccents.vantageDark,
  accentSubtle: productAccents.vantageSubtle,
  link: productAccents.vantageLink,
  icon: productAccents.vantageIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.vantageDark,
  borderFocus: productAccents.vantageDark,
  borderActive: productAccents.vantageDark,
});
const insightsLight = createTheme(colors, {
  tabLabel: productAccents.insightsLight,
  segmentBorder: productAccents.insightsLight,
  segmentSelected: productAccents.insightsSubtle,
  segmentSelectedHover: '#b8dcff',
  alertBackground: `color-mix(in srgb, ${productAccents.insightsHighlight} 10%, transparent)`,
  accent: productAccents.insightsLight,
  accentHover: productAccents.insightsHighlight,
  accentPressed: productAccents.insightsDark,
  navigationTextActive: productAccents.insightsLight,
  accentSubtle: productAccents.insightsSubtle,
  link: productAccents.insightsLight,
  icon: productAccents.insightsIcon,
  iconForeground: productAccents.insightsIcon,
  borderHover: productAccents.insightsLight,
  borderFocus: productAccents.insightsLight,
  borderActive: productAccents.insightsLight,
});
const insightsDark = createTheme(colors, {
  tabLabel: productAccents.insightsDark,
  segmentBorder: productAccents.insightsHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.insightsHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.insightsHighlight} 64%, #151b23)`,
  accent: productAccents.insightsLight,
  accentHover: productAccents.insightsHighlight,
  accentPressed: productAccents.insightsDark,
  accentSubtle: productAccents.insightsSubtle,
  link: productAccents.insightsLight,
  icon: productAccents.insightsIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.insightsDark,
  borderFocus: productAccents.insightsDark,
  borderActive: productAccents.insightsDark,
});

/** Apply one complete product/mode theme at each boundary, including portal roots. */
export const themes = {
  topic: { light: topicLight, dark: topicDark },
  analysis: { light: analysisLight, dark: analysisDark },
  datalab: { light: datalabLight, dark: datalabDark },
  vantage: { light: vantageLight, dark: vantageDark },
  insights: { light: insightsLight, dark: insightsDark },
} satisfies Record<QomponentsTheme, Record<'light' | 'dark', ReturnType<typeof createTheme>>>;
